<template>
  <button
    type="button"
    class="filter-fab"
    :class="{ 'filter-fab--open': expanded }"
    :aria-expanded="expanded"
    aria-label="图谱筛选"
    @click="toggle"
  >
    <i class="iconfont icon-filtercenterfocus" />
    <span v-if="filterActive && !expanded" class="filter-fab__dot" />
  </button>

  <Teleport v-if="overlayTarget" :to="overlayTarget">
    <div class="graph-floating-filter-layer">
      <Transition name="filter-backdrop">
        <div
          v-if="expanded"
          class="filter-backdrop"
          aria-hidden="true"
          @click="collapse"
        />
      </Transition>

      <Transition name="filter-panel">
        <div
          v-if="expanded"
          class="filter-panel"
          role="dialog"
          aria-label="图谱筛选面板"
          @click.stop
        >
          <div class="filter-panel__header">
            <span class="filter-panel__title">筛选</span>
            <button
              type="button"
              class="filter-panel__close"
              aria-label="收起筛选"
              @click="collapse"
            >
              <el-icon><Close /></el-icon>
            </button>
          </div>

          <div class="filter-panel__body">
            <div class="filter-panel__content">
              <slot />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  defineProps,
  defineEmits,
} from "vue";
import { Close } from "@element-plus/icons-vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  filterActive: { type: Boolean, default: false },
  /** 浮层挂载目标（图谱画布容器 DOM） */
  overlayTarget: { type: Object, default: null },
});

const emit = defineEmits(["update:modelValue"]);

const expanded = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const toggle = () => {
  expanded.value = !expanded.value;
};

const collapse = () => {
  expanded.value = false;
};

const onEscape = (e) => {
  if (e.key === "Escape" && expanded.value) {
    collapse();
  }
};

onMounted(() => {
  window.addEventListener("keydown", onEscape);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onEscape);
});
</script>

<style lang="scss" scoped>
.filter-fab {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  color: #4095e5;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: transform 0.28s cubic-bezier(0.34, 1.2, 0.64, 1),
    box-shadow 0.28s ease, background-color 0.2s ease, border-color 0.2s ease;

  .iconfont {
    font-size: 18px;
    transition: transform 0.28s ease;
  }

  &:hover {
    background: #fff;
    border-color: #93c5fd;
    box-shadow: 0 4px 14px rgba(64, 149, 229, 0.18);
  }

  &--open {
    background: #4095e5;
    border-color: #4095e5;
    color: #fff;
    box-shadow: 0 4px 14px rgba(64, 149, 229, 0.3);

    .iconfont {
      transform: rotate(90deg);
    }
  }

  &__dot {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #f56c6c;
    border: 2px solid #fff;
  }
}
</style>

<style lang="scss">
.graph-floating-filter-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 20;
}

.graph-floating-filter-layer .filter-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.12);
  pointer-events: auto;
  z-index: 1;
}

.graph-floating-filter-layer .filter-panel {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 2;
  pointer-events: auto;
  width: min(400px, calc(100% - 24px));
  max-height: min(72vh, 560px);
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.14);
  backdrop-filter: blur(12px);
  overflow: hidden;
}

.graph-floating-filter-layer .filter-panel__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.graph-floating-filter-layer .filter-panel__title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.graph-floating-filter-layer .filter-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.graph-floating-filter-layer .filter-panel__close:hover {
  background: #f1f5f9;
  color: #334155;
}

.graph-floating-filter-layer .filter-panel__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.graph-floating-filter-layer .filter-panel__content {
  padding: 4px 0 12px;
}

.filter-backdrop-enter-active,
.filter-backdrop-leave-active {
  transition: opacity 0.25s ease;
}

.filter-backdrop-enter-from,
.filter-backdrop-leave-to {
  opacity: 0;
}

.filter-panel-enter-active,
.filter-panel-leave-active {
  transition: opacity 0.28s ease,
    transform 0.28s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.filter-panel-enter-from,
.filter-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

@media (max-width: 768px) {
  .graph-floating-filter-layer .filter-panel {
    width: calc(100% - 16px);
    max-height: 65vh;
  }
}
</style>
